import { Helmet } from "react-helmet-async";
import React, { useCallback, useContext, useEffect, useReducer, useState } from "react";
import { Data } from "../../Data";
import Axios from "axios";
import { toast } from "sonner";
import { getError } from "../../utils/utils";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogBody, DialogFooter, DialogCloseButton,
} from "../../components/ui/dialog";

const reducer = (state, action) => {
    switch (action.type) {
        case "FETCH_REQUEST": return { ...state, loading: true };
        case "FETCH_SUCCESS": return { ...state, resData: action.payload, loading: false };
        case "FETCH_FAIL": return { ...state, loading: false };
        default: return state;
    }
};

function RequestCard({ item, categories, states }) {
    const [expanded, setExpanded] = useState(false);
    const stateObj = states?.find((s) => parseInt(s.id) === parseInt(item.stav));
    const categoryObj = categories?.find((c) => parseInt(c.id) === parseInt(item.kategorie));
    const hasAttachment = item.priloha && !["undefined", "null", ""].includes(item.priloha);

    return (
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
            <button
                className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-accent/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
            >
                <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2.5">
                    <span className="font-mono text-xs text-muted-foreground">#{item.id}</span>
                    {categoryObj && (
                        <span className="text-xs font-medium text-foreground">{categoryObj.nazev_kategorie}</span>
                    )}
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                        {new Date(item.datum_vytvoreni).toLocaleDateString("cs-CZ")}
                    </span>
                    {stateObj && (
                        <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border"
                            style={{ backgroundColor: stateObj.color + "20", color: stateObj.color, borderColor: stateObj.color + "40" }}
                        >
                            {stateObj.nazev_stavu}
                        </span>
                    )}
                </div>
                <svg
                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                    strokeWidth={2} stroke="currentColor"
                    className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
                    aria-hidden="true"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
            </button>

            {expanded && (
                <div className="border-t border-border px-5 py-4 space-y-2.5 text-sm">
                    <div className="flex justify-between gap-4 py-1 border-b border-border/50">
                        <span className="text-muted-foreground">Předmět</span>
                        <span className="font-medium text-right">{item.predmet}</span>
                    </div>
                    <div className="py-1 border-b border-border/50">
                        <span className="text-muted-foreground block mb-1">Zpráva</span>
                        <p className="text-foreground text-sm leading-relaxed">{item.text}</p>
                    </div>
                    <div className="flex justify-between gap-4 py-1 border-b border-border/50">
                        <span className="text-muted-foreground">Příloha</span>
                        <span>
                            {hasAttachment ? (
                                <a href={`${process.env.REACT_APP_DATA_URL}/${item.priloha}`} target="_blank" rel="noreferrer" download className="text-[#082359] underline underline-offset-2 font-medium">
                                    Stáhnout přílohu
                                </a>
                            ) : (
                                <span className="text-muted-foreground italic">Bez přílohy</span>
                            )}
                        </span>
                    </div>
                    <div className="flex justify-between gap-4 py-1">
                        <span className="text-muted-foreground">Výsledek</span>
                        <span>
                            {item.odpoved === undefined ? (
                                <span className="text-muted-foreground italic">Čeká na odpověď</span>
                            ) : (
                                <span>
                                    <em className="text-muted-foreground">{new Date(item.odpoved.rdate).toLocaleDateString("cs-CZ")}</em>
                                    {" – "}{item.odpoved.remark}
                                </span>
                            )}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

function NewRequestModal({ open, onClose, resData, userToken, onSuccess }) {
    const [file, setFile] = useState(null);
    const [refNo, setRefNo] = useState("");
    const [category, setCategory] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    const reset = () => { setFile(null); setRefNo(""); setCategory(""); setSubject(""); setMessage(""); };

    const handleClose = () => { reset(); onClose(); };

    const sendForm = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("refNo", refNo);
            formData.append("category", category);
            formData.append("subject", subject);
            formData.append("message", message);
            await Axios.post("/api/request/form", formData, {
                headers: { authorization: `Bearer ${userToken}` },
            });
            toast.success("Žádost byla úspěšně odeslána!");
            handleClose();
            onSuccess();
        } catch (err) {
            toast.error(getError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent size="md" className="relative">
                <DialogCloseButton />
                <DialogHeader>
                    <DialogTitle>Nová žádost</DialogTitle>
                </DialogHeader>
                <DialogBody>
                    <form id="new-request-form" onSubmit={sendForm} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="form-refno" className="mb-1.5 block text-sm">Referenční číslo</Label>
                                <Select id="form-refno" required value={refNo} onChange={(e) => setRefNo(e.target.value)}>
                                    <option hidden value="" />
                                    {resData?.cumulativeRefNo?.map((item, i) => (
                                        <option key={i} value={item}>{item}</option>
                                    ))}
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="form-category" className="mb-1.5 block text-sm">Kategorie</Label>
                                <Select id="form-category" required value={category} onChange={(e) => setCategory(e.target.value)}>
                                    <option hidden value="" />
                                    {resData?.categories?.map((item) => (
                                        <option key={item.id} value={item.id}>{item.nazev_kategorie}</option>
                                    ))}
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="form-subject" className="mb-1.5 block text-sm">Předmět</Label>
                            <Input id="form-subject" value={subject} onChange={(e) => setSubject(e.target.value)} required minLength={5} maxLength={50} type="text" />
                        </div>
                        <div>
                            <Label htmlFor="form-message" className="mb-1.5 block text-sm">Zpráva</Label>
                            <Textarea id="form-message" value={message} onChange={(e) => setMessage(e.target.value)} required minLength={10} maxLength={500} rows={4} />
                        </div>
                        <div>
                            <Label htmlFor="form-file" className="mb-1.5 block text-sm">Příloha <span className="text-muted-foreground font-normal">(volitelná)</span></Label>
                            <Input id="form-file" accept=".png,.jpeg,.jpg,.pdf,.zip" onChange={(e) => setFile(e.target.files[0])} type="file" />
                        </div>
                    </form>
                </DialogBody>
                <DialogFooter>
                    <div className="flex items-center gap-2 justify-end w-full">
                        <Button variant="outline" type="button" onClick={handleClose} disabled={sending}>Zrušit</Button>
                        <Button type="submit" form="new-request-form" disabled={sending}>
                            {sending ? "Odesílám…" : "Odeslat žádost"}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function RequestsSkeleton() {
    return (
        <div className="space-y-3">
            {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-border p-5 shadow-sm flex items-center gap-4">
                    <div className="flex-1 flex gap-3 items-center">
                        <Skeleton className="h-4 w-10" />
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-5 w-20 rounded-full" />
                    </div>
                    <Skeleton className="h-4 w-4" />
                </div>
            ))}
        </div>
    );
}

const CLOSED_STATUSES = ["Ukončeno", "Ukončeno bez řešení"];

function requestLabel(count) {
    if (count === 1) return "1 žádost";
    if (count >= 2 && count <= 4) return `${count} aktivní žádosti`;
    return `${count} aktivních žádostí`;
}

export default function Requests() {
    const { state } = useContext(Data);
    const { userInfo } = state;
    const [modalOpen, setModalOpen] = useState(false);
    const [showClosed, setShowClosed] = useState(false);
    const [{ loading, resData }, dispatch] = useReducer(reducer, { resData: null, loading: true });

    const updateData = useCallback(async () => {
        dispatch({ type: "FETCH_REQUEST" });
        try {
            const { data } = await Axios.post("/api/request/", { request: "info" }, {
                headers: { authorization: `Bearer ${userInfo.token}` },
            });
            dispatch({ type: "FETCH_SUCCESS", payload: data });
        } catch (err) {
            dispatch({ type: "FETCH_FAIL" });
            toast.error(getError(err));
        }
    }, [userInfo]);

    useEffect(() => { updateData(); }, [updateData]);

    const allRequests = (resData?.requests || []).slice().sort(
        (a, b) => new Date(b.datum_vytvoreni) - new Date(a.datum_vytvoreni)
    );

    const getStateName = (item) => {
        const stateObj = resData?.states?.find((s) => parseInt(s.id) === parseInt(item.stav));
        return stateObj?.nazev_stavu ?? "";
    };

    const activeRequests = allRequests.filter((r) => !CLOSED_STATUSES.includes(getStateName(r)));
    const closedRequests = allRequests.filter((r) => CLOSED_STATUSES.includes(getStateName(r)));

    return (
        <>
            <Helmet><title>Žádosti | De Vries Group, a.s.</title></Helmet>
            <main className="py-8 px-6 max-w-5xl mx-auto">
                <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                    <div>
                        <h1 className="text-2xl font-semibold text-foreground">Žádosti</h1>
                        {loading
                            ? <Skeleton className="mt-1.5 h-3.5 w-20" />
                            : <p className={`text-sm mt-0.5 ${activeRequests.length === 0 ? "text-muted-foreground" : "text-muted-foreground"}`}>
                                {activeRequests.length === 0 ? "Žádné aktivní žádosti" : requestLabel(activeRequests.length)}
                            </p>
                        }
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {loading ? (
                            <>
                                <Skeleton className="h-9 w-24 rounded-md" />
                                <Skeleton className="h-9 w-32 rounded-md" />
                            </>
                        ) : (
                            <>
                                {closedRequests.length > 0 && (
                                    <button
                                        onClick={() => setShowClosed((v) => !v)}
                                        className={`relative inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${showClosed ? "bg-white border-gray-400 text-gray-800 shadow-sm hover:bg-gray-50" : "bg-transparent border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400"}`}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
                                        </svg>
                                        Archiv
                                        <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-semibold leading-none px-1 ${showClosed ? "bg-gray-200 text-gray-700" : "bg-gray-200 text-gray-400"}`}>
                                            {closedRequests.length}
                                        </span>
                                    </button>
                                )}
                                <Button onClick={() => setModalOpen(true)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 mr-1.5" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                    </svg>
                                    Nová žádost
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <RequestsSkeleton />
                ) : (
                    <div className="space-y-6">
                        {/* Active requests */}
                        <div className="space-y-3">
                            {activeRequests.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-gray-200 p-10 text-center">
                                    <p className="text-sm text-muted-foreground">Nemáte žádné aktivní žádosti</p>
                                </div>
                            ) : (
                                activeRequests.map((item) => (
                                    <RequestCard key={item.id} item={item} categories={resData.categories} states={resData.states} />
                                ))
                            )}
                        </div>

                        {/* Closed requests */}
                        {showClosed && closedRequests.length > 0 && (
                            <section>
                                <h2 className="text-base font-semibold text-foreground mb-3">Ukončené žádosti</h2>
                                <div className="space-y-3">
                                    {closedRequests.map((item) => (
                                        <RequestCard key={item.id} item={item} categories={resData.categories} states={resData.states} />
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>
                )}
            </main>

            <NewRequestModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                resData={resData}
                userToken={userInfo.token}
                onSuccess={updateData}
            />
        </>
    );
}
